/* eslint-disable no-console */
import { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { logout, LogoutReason } from '../actions/authActions'
import { setOfflineMode } from '../actions/offlineActions'
import { AlphaprocessDatabase } from '../offlineMode/offlineDb'
import { store } from '../store'

export interface IPaginatedData<T> {
  data: T[]
  currentPage: number
  totalPages: number
  totalCount: number
}
const FORCED_LOGOUT_REASONS: LogoutReason[] = [
  'account_deactivated',
  'account_locked',
  'account_unconfirmed',
  'session_expired'
]

// Delays before each connectivity check after a network error. A single failed request
// (flaky mobile connection, short server restart) must not switch the app to offline mode.
const OFFLINE_CHECK_DELAYS_MS = [0, 3000, 10000]
const OFFLINE_CHECK_TIMEOUT_MS = 8000

let offlineCheck: Promise<void> | null = null

export class ApiBase {
  private client: AxiosInstance

  protected offlineDb: AlphaprocessDatabase

  constructor(client: AxiosInstance) {
    this.client = client
    this.offlineDb = new AlphaprocessDatabase()
  }

  // the raw client, for resending queued answers without the offline routing (pendingResults)
  protected get http(): AxiosInstance {
    return this.client
  }

  // get/post/put answer undefined for a network error: callers like resultApi then keep the request for
  // later. A read that needs the answer gets a clear connection error instead of a TypeError on undefined,
  // so the page says "not reachable" and retries by itself (ErrorView)
  protected static ensureResponse<T>(response: AxiosResponse<T> | undefined): AxiosResponse<T> {
    if (!response) throw new Error('Network Error')
    return response
  }

  protected isOffline(): boolean {
    return store.getState().core.isOffline
  }

  private static isNetWorkError(error: AxiosError): boolean {
    const { message, response } = error as AxiosError
    return message === 'Network Error' && !response
  }

  // Switches to offline mode only if the server stays unreachable for several checks.
  // Any response from the server (even an error status) means we are online.
  private static confirmOffline(client: AxiosInstance): void {
    if (offlineCheck || store.getState().core.isOffline) {
      return
    }

    offlineCheck = (async () => {
      for (const delay of OFFLINE_CHECK_DELAYS_MS) {
        await new Promise((resolve) => setTimeout(resolve, delay))
        try {
          await client.get('/ping', { timeout: OFFLINE_CHECK_TIMEOUT_MS })
          return
        } catch (e) {
          if ((e as AxiosError).response) {
            return
          }
        }
      }
      store.dispatch(setOfflineMode(true))
    })().finally(() => {
      offlineCheck = null
    })
  }

  private static checkError(error: AxiosError, client: AxiosInstance): void {
    const { message, response } = error as AxiosError

    console.log('AXIOS ERROR MESSAGE', message)
    console.log('AXIOS ERROR RESPONSE', response)

    if (this.isNetWorkError(error)) {
      this.confirmOffline(client)
      return
    }

    if (response?.status === 401) {
      const jwt = store.getState().core.jwt
      const errorCode = (response.data as any)?.error_code as string | undefined

      // The backend rejected the session itself (account deleted/locked, token revoked, ...).
      // Log out and show the reason on the login page instead of leaving the user on a page
      // with a misleading error. Guarded by jwt so the logout requests themselves
      // (which fail with the same 401) can't trigger another logout.
      if (jwt && errorCode && FORCED_LOGOUT_REASONS.includes(errorCode as LogoutReason)) {
        store.dispatch(logout(errorCode as LogoutReason))
        return
      }

      if (jwt) {
        try {
          const payload = JSON.parse(atob(jwt.split('.')[1]))
          if (payload.exp && Date.now() >= payload.exp * 1000) {
            store.dispatch(logout())
          }
        } catch (e) {
          // JWT could not be decoded — let the error propagate normally
        }
      }
    }
  }

  // eslint-disable-next-line consistent-return
  protected async get<T>(url: string, params?: any): Promise<AxiosResponse<T>> {
    try {
      return await this.client.get<T>(url, { params })
    } catch (error) {
      const ae = error as AxiosError
      ApiBase.checkError(ae, this.client)
      if (!ApiBase.isNetWorkError(ae)) {
        throw error
      }
    }
  }

  // eslint-disable-next-line consistent-return
  protected async post<T>(url: string, data?: any): Promise<AxiosResponse<T>> {
    try {
      return await this.client.post<T>(url, data)
    } catch (error) {
      const ae = error as AxiosError
      ApiBase.checkError(ae, this.client)
      if (!ApiBase.isNetWorkError(ae)) {
        throw error
      }
    }
  }

  // eslint-disable-next-line consistent-return
  protected async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      return await this.client.put<T>(url, data, config)
    } catch (error) {
      const ae = error as AxiosError
      ApiBase.checkError(ae, this.client)
      if (!ApiBase.isNetWorkError(ae)) {
        throw error
      }
    }
  }

  // eslint-disable-next-line consistent-return
  protected async delete<T>(url: string): Promise<AxiosResponse<T>> {
    try {
      return await this.client.delete<T>(url)
    } catch (error) {
      const ae = error as AxiosError
      ApiBase.checkError(ae, this.client)
      if (!ApiBase.isNetWorkError(ae)) {
        throw error
      }
    }
  }

  protected paginateResponse<T>(response: AxiosResponse<T[]>): IPaginatedData<T> {
    return {
      data: response.data,
      currentPage: parseInt(response.headers['current-page']),
      totalPages: parseInt(response.headers['total-count']) > 0
        ? parseInt(response.headers['total-pages']) : 0,
      totalCount: parseInt(response.headers['total-count'])
    }
  }
}
